// src/components/backend/companies/hooks/useCompanyForm.js
import { useCallback, useState } from "react";
import { normalizeIdArray } from "../backend/companies/utils/companyUtils";

export default function useCompanyForm() {

    const blankForm = useCallback(() => {
        return {
            name: "",
            slug: "",
            customer_number: "",

            contract_date: "",
            salutation: "",
            business_name: "",
            contact_full_name: "",

            address_info: "",
            street: "",
            street2: "",
            zip: "",
            city: "",
            canton_state: "",
            country: "Germany",

            phone1: "",
            phone2: "",
            mobile_sms: "",
            telefax: "",
            email: "",
            reservations_email: "",

            facebook: "",
            instagram: "",
            twitter: "",
            homepage: "",

            hotel_extra_info: "",
            homepage_mobile_optimized: false,

            customer_link_reservation: "",
            customer_link_virtual_tour: "",
            customer_link_video: "",
            customer_link_webcam: "",

            closing_days: "",
            opening_hours: "",
            summer_winter_season: "",
            company_holidays: "",
            info: "",
            seo_meta_description: "",
            seo_meta_keywords: "",

            billing_company: "",
            billing_contact: "",
            billing_extra_info: "",
            billing_street: "",
            billing_zip: "",
            billing_city: "",
            billing_country: "",
            billing_currency: "",

            latitude: "",
            longitude: "",

            category_ids: [],
            amenity_ids: [],
            hotel_group_ids: [],
            award_ids: [],
            logo_ids: [],
            cuisine_ids: [],
            restaurant_price_ids: [],
            delivery_service_ids: [],
            location_ids: [],
            leisure_activity_ids: [],
            region_ids: [],
            classification_assignments: [],
            room_quantity_definitions: [],
            room_detail_definitions: [],
            room_price_definitions: [],
            room_price_special_definitions: [],
            room_quantities: [],
            room_details: [],
            room_price_settings: {
                room_prices_url: "",
                currency: "CHF",
                breakfast_option: "",
                room_info: "",
            },
            room_prices: [],
            room_price_specials: [],
            is_active: true,
        };
    }, []);

    const [form, setForm] = useState(blankForm());

    const applyCompanyToForm = useCallback((companyLike) => {
        const c = companyLike || {};

        setForm({
            name: c.name || "",
            slug: c.slug || "",
            customer_number: c.customer_number || "",

            contract_date: c.contract_date || "",
            salutation: c.salutation || "",
            business_name: c.business_name || "",
            contact_full_name: c.contact_full_name || "",

            address_info: c.address_info || "",
            street: c.street || "",
            street2: c.street2 || "",
            zip: c.zip || "",
            city: c.city || "",
            canton_state: c.canton_state || "",
            country: c.country || "Germany",

            phone1: c.phone1 || "",
            phone2: c.phone2 || "",
            mobile_sms: c.mobile_sms || "",
            telefax: c.telefax || "",
            email: c.email || "",
            reservations_email: c.reservations_email || "",

            facebook: c.facebook || "",
            instagram: c.instagram || "",
            twitter: c.twitter || "",
            homepage: c.homepage || "",

            hotel_extra_info: c.hotel_extra_info || "",
            homepage_mobile_optimized: !!c.homepage_mobile_optimized,

            customer_link_reservation: c.customer_link_reservation || "",
            customer_link_virtual_tour: c.customer_link_virtual_tour || "",
            customer_link_video: c.customer_link_video || "",
            customer_link_webcam: c.customer_link_webcam || "",

            closing_days: c.closing_days || "",
            opening_hours: c.opening_hours || "",
            summer_winter_season: c.summer_winter_season || "",
            company_holidays: c.company_holidays || "",
            info: c.info || "",
            seo_meta_description: c.seo_meta_description || "",
            seo_meta_keywords: c.seo_meta_keywords || "",

            billing_company: c.billing_company || "",
            billing_contact: c.billing_contact || "",
            billing_extra_info: c.billing_extra_info || "",
            billing_street: c.billing_street || "",
            billing_zip: c.billing_zip || "",
            billing_city: c.billing_city || "",
            billing_country: c.billing_country || "",
            billing_currency: c.billing_currency || "",

            latitude: c.latitude ?? "",
            longitude: c.longitude ?? "",

            category_ids: normalizeIdArray(c.category_ids || []),
            amenity_ids: normalizeIdArray(c.amenity_ids || []),
            hotel_group_ids: normalizeIdArray(c.hotel_group_ids || []),
            award_ids: normalizeIdArray(c.award_ids || []),
            logo_ids: normalizeIdArray(c.logo_ids || []),
            cuisine_ids: normalizeIdArray(c.cuisine_ids || []),
            restaurant_price_ids: normalizeIdArray(c.restaurant_price_ids || []),
            delivery_service_ids: normalizeIdArray(c.delivery_service_ids || []),
            location_ids: normalizeIdArray(c.location_ids || []),
            leisure_activity_ids: normalizeIdArray(c.leisure_activity_ids || []),
            region_ids: normalizeIdArray(c.region_ids || []),
            classification_assignments: (Array.isArray(c.classifications) ? c.classifications : []).map((item, index) => ({
                classification_id: Number(item.classification_id || item.id),
                value: item.value ?? "",
                is_superior: !!item.is_superior,
                is_primary: !!item.is_primary,
                sort_order: Number(item.sort_order ?? index),
            })).filter((item) => Number.isFinite(item.classification_id)),
            room_quantity_definitions: Array.isArray(c.room_quantity_definitions) ? c.room_quantity_definitions : [],
            room_detail_definitions: Array.isArray(c.room_detail_definitions) ? c.room_detail_definitions : [],
            room_price_definitions: Array.isArray(c.room_price_definitions) ? c.room_price_definitions : [],
            room_price_special_definitions: Array.isArray(c.room_price_special_definitions) ? c.room_price_special_definitions : [],
            room_quantities: (Array.isArray(c.room_quantities) ? c.room_quantities : []).map((item) => ({
                room_quantity_id: Number(item.room_quantity_id || item.id),
                quantity: item.quantity ?? "",
            })).filter((item) => Number.isFinite(item.room_quantity_id)),
            room_details: (Array.isArray(c.room_details) ? c.room_details : []).map((item) => ({
                room_detail_id: Number(item.room_detail_id || item.id),
                custom_label: item.custom_label || item.label_de || "",
            })).filter((item) => Number.isFinite(item.room_detail_id)),
            room_price_settings: {
                room_prices_url: c.room_price_settings?.room_prices_url || "",
                currency: c.room_price_settings?.currency || "CHF",
                breakfast_option: c.room_price_settings?.breakfast_option ?? "",
                room_info: c.room_price_settings?.room_info || "",
            },
            room_prices: (Array.isArray(c.room_prices) ? c.room_prices : []).map((item) => ({
                room_price_id: Number(item.room_price_id || item.id),
                custom_label: item.custom_label || "",
                reference: item.reference || "",
                price_min: item.price_min || "",
                price_max: item.price_max || "",
                is_from: !!item.is_from,
            })).filter((item) => Number.isFinite(item.room_price_id)),
            room_price_specials: (Array.isArray(c.room_price_specials) ? c.room_price_specials : []).map((item) => ({
                special_id: Number(item.special_id || item.id),
                custom_label: item.custom_label || item.label_de || "",
            })).filter((item) => Number.isFinite(item.special_id)),
            is_active: c.is_active !== undefined ? !!c.is_active : true,
        });
    }, []);

    const validateBase = useCallback((f, custNoRe) => {

        if (!String(f?.name || "").trim()) {
            return "Firmenname ist Pflichtfeld.";
        }

        const cust = String(f?.customer_number || "").trim();
        if (cust && custNoRe && !custNoRe.test(cust)) {
            return "Kundennummer darf nur Buchstaben/Zahlen sowie _ und - enthalten.";
        }

        const lat = f?.latitude ? Number(String(f.latitude).replace(",", ".")) : null;
        const lon = f?.longitude ? Number(String(f.longitude).replace(",", ".")) : null;

        if (f?.latitude && !Number.isFinite(lat)) {
            return "Breitengrad ist keine gültige Zahl.";
        }

        if (f?.longitude && !Number.isFinite(lon)) {
            return "Längengrad ist keine gültige Zahl.";
        }

        const invalidQuantity = (f?.room_quantities || []).some((item) => {
            if (item?.quantity === "" || item?.quantity === null || item?.quantity === undefined) return false;
            const value = Number(item.quantity);
            return !Number.isInteger(value) || value < 0;
        });
        if (invalidQuantity) {
            return "Zimmeranzahlen müssen ganze, positive Zahlen sein.";
        }

        return "";
    }, []);

    const buildPayloadBase = useCallback((f) => {

        const lat = f?.latitude ? Number(String(f.latitude).replace(",", ".")) : null;
        const lon = f?.longitude ? Number(String(f.longitude).replace(",", ".")) : null;

        return {
            ...f,

            name: String(f?.name || "").trim(),
            customer_number: String(f?.customer_number || "").trim() || null,

            street: f?.street || null,
            street2: f?.street2 || null,
            zip: f?.zip || null,
            city: f?.city || null,
            country: f?.country || null,
            seo_meta_description: String(f?.seo_meta_description || "").trim() || null,
            seo_meta_keywords: String(f?.seo_meta_keywords || "").trim() || null,

            latitude: Number.isFinite(lat) ? lat : null,
            longitude: Number.isFinite(lon) ? lon : null,

            category_ids: normalizeIdArray(f?.category_ids || []),
            amenity_ids: normalizeIdArray(f?.amenity_ids || []),
            hotel_group_ids: normalizeIdArray(f?.hotel_group_ids || []),
            award_ids: normalizeIdArray(f?.award_ids || []),
            logo_ids: normalizeIdArray(f?.logo_ids || []),
            cuisine_ids: normalizeIdArray(f?.cuisine_ids || []).slice(0, 5),
            restaurant_price_ids: normalizeIdArray(f?.restaurant_price_ids || []),
            delivery_service_ids: normalizeIdArray(f?.delivery_service_ids || []),
            location_ids: normalizeIdArray(f?.location_ids || []),
            leisure_activity_ids: normalizeIdArray(f?.leisure_activity_ids || []),
            region_ids: normalizeIdArray(f?.region_ids || []),
            classification_assignments: Array.isArray(f?.classification_assignments)
                ? f.classification_assignments
                : [],
            room_quantities: (Array.isArray(f?.room_quantities) ? f.room_quantities : [])
                .filter((item) => String(item?.quantity ?? "").trim() !== "")
                .map((item) => ({
                    room_quantity_id: Number(item.room_quantity_id || item.id),
                    quantity: Number(item.quantity),
                }))
                .filter((item) => Number.isFinite(item.room_quantity_id) && Number.isInteger(item.quantity) && item.quantity >= 0),
            room_details: (Array.isArray(f?.room_details) ? f.room_details : [])
                .map((item) => ({
                    room_detail_id: Number(item.room_detail_id || item.id),
                    custom_label: String(item.custom_label || "").trim() || null,
                }))
                .filter((item) => Number.isFinite(item.room_detail_id)),
            room_price_settings: {
                room_prices_url: String(f?.room_price_settings?.room_prices_url || "").trim() || null,
                currency: String(f?.room_price_settings?.currency || "CHF").trim() || "CHF",
                breakfast_option: f?.room_price_settings?.breakfast_option === "" ? null : f?.room_price_settings?.breakfast_option,
                room_info: String(f?.room_price_settings?.room_info || "").trim() || null,
            },
            room_prices: (Array.isArray(f?.room_prices) ? f.room_prices : [])
                .map((item) => ({
                    room_price_id: Number(item.room_price_id || item.id),
                    custom_label: String(item.custom_label || "").trim() || null,
                    reference: String(item.reference || "").trim() || null,
                    price_min: String(item.price_min || "").trim() || null,
                    price_max: String(item.price_max || "").trim() || null,
                    is_from: !!item.is_from,
                }))
                .filter((item) => Number.isFinite(item.room_price_id) && (
                    item.custom_label || item.reference || item.price_min || item.price_max || item.is_from
                )),
            room_price_specials: (Array.isArray(f?.room_price_specials) ? f.room_price_specials : [])
                .map((item) => ({
                    special_id: Number(item.special_id || item.id),
                    custom_label: String(item.custom_label || "").trim() || null,
                }))
                .filter((item) => Number.isFinite(item.special_id)),

            is_active: !!f?.is_active,
        };
    }, []);

    return {
        form,
        setForm,
        blankForm,
        applyCompanyToForm,
        validateBase,
        buildPayloadBase,
    };
}
